<template>
  <div class="version-display" v-if="versionInfo">
    <span class="version-text">{{ displayVersion }}</span>
    <span
      v-if="hasNewVersion"
      class="update-badge"
      @click="showUpdateDialog"
    >
      新版本
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getLatestVersion } from '@/api/version'
import type { VersionInfo } from '@/types/version'
import logger from '@/utils/logger'

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5分钟轮询间隔

const versionInfo = ref<VersionInfo | null>(null)
const hasNewVersion = ref(false)
let pollingTimer: ReturnType<typeof setInterval> | null = null
let currentGitCommit = ''

// 计算显示的版本号（简化显示）
const displayVersion = computed(() => {
  if (!versionInfo.value) return ''

  const { fullVersion, versionType } = versionInfo.value

  // 正式版本：直接显示 v1.2.3
  if (versionType === 'release' || (fullVersion && !fullVersion.includes('-dev.'))) {
    return fullVersion
  }

  // 开发版本：显示简化格式 v1.2.3-dev.{date}.{commit_short}
  // 修正regex匹配实际格式：v1.2.4-dev.20240626.386
  if (fullVersion && fullVersion.includes('-dev.')) {
    const match = fullVersion.match(/^(v\d+\.\d+\.\d+-dev\.) (\d+)\.([a-f0-9]{6,})$/i)
    if (match) {
      // 显示：v1.2.4-dev.{commit_short}
      return `${match[1]}${match[3]}`
    }
    // 如果格式不匹配，显示前15个字符
    return fullVersion.length > 15 ? `${fullVersion.substring(0, 15)}...` : fullVersion
  }

  // 默认返回完整版本；无版本信息时隐藏（显示英文 Unknown 观感突兀，用户 2026-09-10 反馈）
  return fullVersion || ''
})

// 加载版本信息
const loadVersion = async () => {
  try {
    // 动态获取环境：根据当前运行环境决定查询prod还是dev版本
    const env = import.meta.env.MODE === 'production' ? 'prod' : 'dev'
    const data = await getLatestVersion(env)

    // 检查是否有新版本：使用gitCommit比较更稳定
    if (versionInfo.value && data.gitCommit && data.gitCommit !== currentGitCommit) {
      hasNewVersion.value = true
      ElMessage.info(`检测到新版本: ${data.fullVersion}`)
    }

    // 保存当前git commit用于比较
    if (data.gitCommit) {
      currentGitCommit = data.gitCommit
    }

    versionInfo.value = data
  } catch (error) {
    logger.error('加载版本信息失败:', error)
    // 静默失败，不影响页面使用
  }
}

// 显示更新对话框
const showUpdateDialog = () => {
  ElMessage.confirm(
    '检测到新版本，是否立即刷新页面获取最新功能？',
    '版本更新提示',
    {
      confirmButtonText: '立即刷新',
      cancelButtonText: '稍后提醒',
      type: 'info',
    }
  ).then(() => {
    location.reload()
  }).catch(() => {
    // 用户选择稍后提醒
  })
}

// 处理页面可见性变化
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && versionInfo.value === null) {
    // 页面重新可见且无版本信息时，立即加载
    loadVersion()
  }
}

// 启动轮询（仅在页面可见时）
const startPolling = () => {
  // 页面不可见时不启动轮询
  if (document.visibilityState !== 'visible') return

  pollingTimer = setInterval(() => {
    // 页面可见时才执行轮询
    if (document.visibilityState === 'visible') {
      loadVersion().catch(err => {
        logger.warn('轮询版本信息失败:', err)
      })
    }
  }, POLL_INTERVAL_MS)
}

onMounted(() => {
  // 首次加载版本信息
  loadVersion()

  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // 启动轮询
  startPolling()
})

onUnmounted(() => {
  // 清理轮询定时器
  if (pollingTimer !== null) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }

  // 移除页面可见性监听
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped>
.version-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
}

.version-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-family: 'Monaco', 'Menlo', monospace;
}

.update-badge {
  font-size: 11px;
  padding: 2px 8px;
  background-color: var(--color-primary);
  color: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
  user-select: none;
}

.update-badge:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.update-badge:active {
  transform: scale(0.95);
}
</style>


<style scoped>
.version-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
}

.version-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-family: 'Monaco', 'Menlo', monospace;
}

.update-badge {
  font-size: 11px;
  padding: 2px 8px;
  background-color: var(--color-primary);
  color: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all var(--transition-base) var(--transition-easing);
}

.update-badge:hover {
  opacity: 0.8;
  transform: scale(1.05);
}
</style>
